import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Package, Globe, Users, Award } from 'lucide-react';

const FALLBACK_BANNER_IMAGE = "https://images.unsplash.com/photo-1542831371-29b0f74f9713?w=1600&q=80";

const getBannerImageUrl = (url, w = 1280) => {
  if (!url || typeof url !== 'string' || url.trim() === '') return FALLBACK_BANNER_IMAGE;
  if (url.startsWith('http')) return url;
  const path = url.startsWith('/') ? url : `/${url}`;
  // Backend returns a resized/compressed WebP for ?w= — far lighter than the
  // full-size original. (Tiny width used for the blurred backdrop copy.)
  return path.startsWith('/uploads') ? `${path}?w=${w}` : path;
};

const getStatIcon = (iconName) => {
  switch (iconName) {
    case 'Package': return <Package size={24} />;
    case 'Globe': return <Globe size={24} />;
    case 'Users': return <Users size={24} />;
    case 'Award': return <Award size={24} />;
    default: return <Package size={24} />;
  }
};

const BannerStatsOverlay = ({ stats }) => (
  <div className="container mx-auto px-3 sm:px-4 lg:px-8 relative z-20">
    <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6 -mt-6 sm:-mt-10 md:-mt-16">
      {stats.map((stat, idx) => (
        <motion.div
          key={stat.id || idx}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: idx * 0.1 }}
          viewport={{ once: true }}
          className="bg-white rounded-[1.25rem] sm:rounded-[2rem] p-3 sm:p-6 md:p-8 flex min-h-[5.5rem] sm:min-h-0 items-center gap-2.5 sm:gap-5 shadow-2xl shadow-slate-200/50 border-b-4 transition-transform hover:-translate-y-1"
          style={{ borderBottomColor: stat.color || (idx % 2 === 0 ? '#3b82f6' : '#f97316') }}
        >
          <div className={`p-2.5 sm:p-4 rounded-xl sm:rounded-2xl shrink-0 ${idx % 2 === 0 ? 'bg-blue-50 text-blue-600' : 'bg-orange-50 text-orange-600'}`}>
            <div className="scale-[0.7] sm:scale-100">{getStatIcon(stat.icon)}</div>
          </div>
          <div className="flex flex-col min-w-0">
            <span className="text-lg sm:text-2xl md:text-3xl font-black tracking-tighter text-slate-900 leading-none mb-0.5 sm:mb-1 truncate">
              {stat.value}
            </span>
            <span className="text-[8px] sm:text-[10px] md:text-xs font-black uppercase tracking-[0.08em] sm:tracking-[0.2em] text-slate-400 leading-tight whitespace-normal">
              {stat.label}
            </span>
          </div>
        </motion.div>
      ))}
    </div>
  </div>
);

const BannerCarousel = ({ banners = [], bannerStats = [], scrollInterval = 3, autoPlay = true, isLoading = false }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const [loadedBannerMap, setLoadedBannerMap] = useState({});

  const activeBanners = useMemo(() => (
    banners
      .filter(b => b.status === 'Active')
      .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
  ), [banners]);

  useEffect(() => {
    if (currentIndex >= activeBanners.length) {
      setCurrentIndex(0);
    }
  }, [currentIndex, activeBanners.length]);

  const getBannerKey = (banner, index) => banner?.id || `${banner?.imageUrl || 'banner'}-${index}`;
  const allBannersLoaded = activeBanners.length > 0 && activeBanners.every((banner, index) => loadedBannerMap[getBannerKey(banner, index)]);

  const preloadBannerAtIndex = useCallback((index, onReady) => {
    const banner = activeBanners[index];
    if (!banner) return;

    const bannerKey = getBannerKey(banner, index);
    if (loadedBannerMap[bannerKey]) {
      onReady?.();
      return;
    }

    const img = new Image();
    const finalize = () => {
      setLoadedBannerMap(prev => (prev[bannerKey] ? prev : { ...prev, [bannerKey]: true }));
      onReady?.();
    };

    img.onload = finalize;
    img.onerror = finalize;
    img.src = getBannerImageUrl(banner.imageUrl);
  }, [activeBanners, loadedBannerMap]);

  const changeBanner = useCallback((nextIndex, nextDirection) => {
    if (activeBanners.length <= 1 || nextIndex === currentIndex) return;

    preloadBannerAtIndex(nextIndex, () => {
      setDirection(nextDirection);
      setCurrentIndex(nextIndex);
    });
  }, [activeBanners.length, currentIndex, preloadBannerAtIndex]);

  const goToNext = useCallback(() => {
    if (activeBanners.length <= 1) return;
    changeBanner((currentIndex + 1) % activeBanners.length, 1);
  }, [activeBanners.length, changeBanner, currentIndex]);

  const goToPrev = useCallback(() => {
    if (activeBanners.length <= 1) return;
    changeBanner((currentIndex - 1 + activeBanners.length) % activeBanners.length, -1);
  }, [activeBanners.length, changeBanner, currentIndex]);

  // Auto-rotation effect
  useEffect(() => {
    if (!autoPlay || !allBannersLoaded || activeBanners.length <= 1) return;

    const timer = setInterval(() => {
      goToNext();
    }, scrollInterval * 1000);

    return () => clearInterval(timer);
  }, [autoPlay, scrollInterval, activeBanners.length, goToNext, allBannersLoaded]);

  useEffect(() => {
    if (activeBanners.length === 0) return;

    let cancelled = false;
    const preloadImage = (banner, index) => {
      const bannerKey = getBannerKey(banner, index);
      if (loadedBannerMap[bannerKey]) return;

      const img = new Image();
      img.onload = () => {
        if (cancelled) return;
        setLoadedBannerMap(prev => (prev[bannerKey] ? prev : { ...prev, [bannerKey]: true }));
      };
      img.onerror = () => {
        if (cancelled) return;
        setLoadedBannerMap(prev => (prev[bannerKey] ? prev : { ...prev, [bannerKey]: true }));
      };
      img.src = getBannerImageUrl(banner.imageUrl);
    };

    activeBanners.forEach(preloadImage);

    return () => {
      cancelled = true;
    };
  }, [activeBanners, loadedBannerMap]);

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 640);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  if (!isLoading && activeBanners.length === 0) return null;

  const currentBanner = activeBanners[currentIndex];
  const imageUrl = getBannerImageUrl(isMobile && currentBanner?.mobileImageUrl ? currentBanner.mobileImageUrl : currentBanner?.imageUrl);
  const currentBannerKey = currentBanner ? getBannerKey(currentBanner, currentIndex) : 'banner-placeholder';
  const isCurrentBannerLoaded = !!loadedBannerMap[currentBannerKey];
  // Show the loader only while the backend data is still loading.
  // Once we have banners, render immediately — no image-load waiting.
  const shouldShowLoader = isLoading;

  const variants = {
    enter: (dir) => ({ x: dir > 0 ? '100%' : '-100%', opacity: 1 }),
    center: { x: 0, opacity: 1 },
    exit: (dir) => ({ x: dir > 0 ? '-100%' : '100%', opacity: 1 }),
  };

  const bannerContent = (
    <img
      src={imageUrl}
      alt={`Banner ${currentIndex + 1}`}
      className="w-full h-full object-contain"
      loading={currentIndex === 0 ? 'eager' : 'lazy'}
      fetchPriority={currentIndex === 0 ? 'high' : 'auto'}
      decoding={currentIndex === 0 ? 'sync' : 'async'}
      onError={(e) => {
        e.target.onerror = null;
        e.target.src = FALLBACK_BANNER_IMAGE;
      }}
    />
  );

  return (
    <>
    <section className="w-full relative bg-white pt-3 sm:pt-5">
      <div className="w-full px-2 sm:px-4">
      <div className={`relative w-full overflow-hidden rounded-[1.5rem] sm:rounded-[2.5rem] ${isMobile && currentBanner?.mobileImageUrl ? 'aspect-square' : 'aspect-[4/4.35] sm:aspect-[16/9] md:aspect-[16/6] lg:aspect-[16/5]'} min-h-[20rem] sm:min-h-0`}>
        <div className={`absolute inset-0 transition-opacity duration-300 ${shouldShowLoader || !currentBanner || !isCurrentBannerLoaded ? 'opacity-100' : 'opacity-0'}`}>
          <div className="absolute inset-0 bg-gradient-to-br from-slate-100 via-slate-50 to-white" />
          {/* Tiny blurred preview (~1KB) shown instantly so the user sees the
              banner blurred immediately instead of an empty box while the full
              image loads (blur-up). */}
          {currentBanner && (
            <img
              src={getBannerImageUrl(isMobile && currentBanner?.mobileImageUrl ? currentBanner.mobileImageUrl : currentBanner?.imageUrl, 32)}
              alt=""
              aria-hidden="true"
              loading="eager"
              fetchPriority="high"
              decoding="async"
              className="absolute inset-0 w-full h-full object-contain blur-2xl scale-110"
            />
          )}
          <div className="absolute inset-0 animate-pulse bg-white/10" />
        </div>

        <AnimatePresence initial={false} custom={direction} mode="sync">
          {currentBanner && (
            <motion.div
              key={currentIndex}
              custom={direction}
              variants={variants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.5, ease: 'easeInOut' }}
              className="absolute inset-0 will-change-transform"
            >
              {currentBanner.url ? (
                <a
                  href={currentBanner.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full h-full cursor-pointer"
                >
                  {bannerContent}
                </a>
              ) : (
                bannerContent
              )}
              {/* Gradient overlay for text readability */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent pointer-events-none" />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Navigation Arrows */}
        {activeBanners.length > 1 && (
          <>
            <button
              onClick={goToPrev}
              className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-slate-700 p-1.5 sm:p-2 rounded-full shadow-lg backdrop-blur-sm transition-all hover:scale-110 z-10"
              aria-label="Previous banner"
            >
              <ChevronLeft size={18} className="sm:w-5 sm:h-5" />
            </button>
            <button
              onClick={goToNext}
              className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-slate-700 p-1.5 sm:p-2 rounded-full shadow-lg backdrop-blur-sm transition-all hover:scale-110 z-10"
              aria-label="Next banner"
            >
              <ChevronRight size={18} className="sm:w-5 sm:h-5" />
            </button>
          </>
        )}

        {/* Dot indicators */}
        {activeBanners.length > 1 && (
          <div className="absolute bottom-6 sm:bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 z-10">
            {activeBanners.map((_, i) => (
              <button
                key={i}
                onClick={() => {
                  changeBanner(i, i > currentIndex ? 1 : -1);
                }}
                className={`transition-all duration-300 rounded-full ${i === currentIndex
                  ? 'w-6 h-2 bg-white shadow-lg'
                  : 'w-2 h-2 bg-white/50 hover:bg-white/70'
                  }`}
                aria-label={`Go to banner ${i + 1}`}
              />
            ))}
          </div>
        )}

        {/* Progress bar for auto-play */}
        {autoPlay && activeBanners.length > 1 && (
          <div className="absolute bottom-0 left-0 right-0 hidden h-[3px] bg-black/10 z-10 sm:block">
            <motion.div
              key={currentIndex}
              className="h-full bg-white/80"
              initial={{ width: '0%' }}
              animate={{ width: '100%' }}
              transition={{ duration: scrollInterval, ease: 'linear' }}
            />
          </div>
        )}
      </div>
      </div>
    </section>
    </>
  );
};

export default BannerCarousel;
